import type { SiteSettingsData } from "./schema";

/**
 * Default site settings used by:
 * - the seed script when bootstrapping the DB
 * - the public API as a fallback if the row is missing
 *
 * Copy is written for a residential developer (társasház-fejlesztés)
 * business. Every field is editable via /admin/site-settings.
 */
export const DEFAULT_SITE_SETTINGS: SiteSettingsData = {
  brand: {
    name: "VATÁK Ablakcentrum Kft.",
    shortName: "VATÁK",
    tagline: "Társasházfejlesztés tudatosan, 2012 óta",
    email: "info@vatak.hu",
    phone: "+36 1 000 0000",
    address: "1051 Budapest, Október 6 utca 14. 1. em. 2A.",
    logoLetter: "V",
    logoImageKey: null,
    primaryColor: "#2f7a3e",
    socials: {
      facebook: null,
      instagram: null,
      linkedin: null,
    },
  },
  home: {
    heroEyebrow: "Lakóingatlan-fejlesztés tudatosan",
    heroTitle: "Új otthon, ami időtálló érték.",
    heroSubtitle:
      "Magas minőségű társasházakat fejlesztünk és építünk a fővárosban. Energiatakarékos műszaki tartalom, átgondolt alaprajzok, transzparens vásárlási folyamat — kulcsátadásig.",
    heroImageKey: null,
    ctaProjectsText: "Aktuális projektek",
    ctaContactText: "Beszéljünk",
    ctaProjectsLink: { linkType: "page", linkValue: "/projektek" },
    ctaContactLink: { linkType: "page", linkValue: "/kapcsolat" },
    servicesEyebrow: "Amivel foglalkozunk",
    servicesTitle: "Lakásfejlesztés egy kézben",
    projectsEyebrow: "Portfólió",
    projectsTitle: "Aktuális és átadott projektek",
    ctaStripTitle: "Otthont keres? Kérjen visszahívást.",
    ctaStripSubtitle:
      "Értékesítő kollégáink hétköznap 9–17 óra között elérhetők.",
    ctaStripButtonText: "Visszahívást kérek",
    ctaStripLink: { linkType: "page", linkValue: "/kapcsolat" },
  },
  about: {
    pageEyebrow: "Rólunk",
    pageTitle: "Több mint tíz év\notthonteremtés Budapesten",
    intro:
      "A VATÁK csapata 2012 óta foglalkozik lakóingatlan-fejlesztéssel a fővárosban. Hisszük, hogy egy jó otthon nem feltétlenül a legdrágább vagy legnagyobb — hanem az, amit a tervezés első percétől átgondolt logika, megbízható kivitelezés és tartós értékálló műszaki tartalom határoz meg.",
    visionTitle: "Vízió",
    visionText:
      "Olyan társasházakat építünk, amelyek 30 év múlva is büszkén állnak. Energiatakarékos szerkezet, modern szigetelés, átgondolt elrendezés és minőségi nyílászárók — az alaptól a tetőig.",
    valuesTitle: "Értékeink",
    values: [
      "Átlátható kommunikáció a vásárlási folyamat minden lépésében",
      "Vállalt határidők, dokumentált műszaki tartalom",
      "Tartós minőség az alapozástól az átadásig",
    ],
    stats: [
      { number: "12+", label: "Év tapasztalat" },
      { number: "180+", label: "Átadott otthon" },
      { number: "8", label: "Befejezett projekt" },
      { number: "3", label: "Aktív projekt" },
    ],
    approachEyebrow: "Megközelítés",
    approachTitle: "Tervezés, építés és értékesítés egy kézben",
    approachText:
      "Saját csapat koordinálja a teljes folyamatot: az engedélyeztetéstől a kivitelezésen és a hitelügyintézésen át a kulcsátadásig. Ez egyetlen kommunikációs csatornát, kiszámítható döntéseket és — hosszú távon — biztosabb minőséget jelent.",
  },
  contact: {
    pageEyebrow: "Kapcsolat",
    pageTitle: "Lépjen velünk kapcsolatba",
    pageSubtitle:
      "Töltse ki az űrlapot, vagy hívjon minket bizalommal — 1 munkanapon belül válaszolunk minden megkeresésre.",
    teamSectionEyebrow: "Csapatunk",
    teamSectionTitle: "Keresse kollégáinkat",
    formSectionEyebrow: "Üzenet",
    formSectionTitle: "Visszahívást kérek",
    privacyPolicyUrl: null,
    privacyPolicyLinkType: null,
  },
  mortgageAdvisor: {
    enabled: true,
    title: "Segítünk a hitelügyintézésben",
    subtitle:
      "Hitelügyintéző kollégánk díjmentesen végigkíséri a teljes folyamatot — a bankválasztástól a folyósításig. Hagyja meg elérhetőségét, és ingyenesen visszahívjuk.",
    ctaText: "Ingyenes visszahívást kérek",
    formIntro:
      "Adja meg elérhetőségét, és kollégánk 1 munkanapon belül keresi Önt.",
    advisorName: "Veres Attila",
    advisorPhone: "+36 70 268 9040",
    // Nem jelenik meg a publikus oldalon: a szerver kiszűri a nyilvános
    // /api/site-settings válaszból, a leadek a központi címre mennek.
    advisorEmail: "vatakhitel@gmail.com",
  },
};

/**
 * Default services seeded on first run. Icon names match Lucide React.
 * Curated for a residential developer (4 standard service categories).
 */
export const DEFAULT_SERVICES = [
  {
    title: "Tervezés",
    description:
      "Építészeti és belsőépítészeti tervezés saját irodával. Energetikai tanúsítás, alaprajz-optimalizálás, anyagválasztási tanácsadás.",
    icon: "Compass",
    sortOrder: 0,
  },
  {
    title: "Kivitelezés",
    description:
      "Saját kivitelezői csapattal és bevált alvállalkozókkal. Folyamatos minőségellenőrzés, vállalt határidőkkel.",
    icon: "HardHat",
    sortOrder: 1,
  },
  {
    title: "Értékesítés",
    description:
      "Közvetlen értékesítés a fejlesztőtől — közvetítői díj nélkül. Foglalási folyamat világos szerződéssel, részletfizetéssel.",
    icon: "KeyRound",
    sortOrder: 2,
  },
  {
    title: "Hitelügyintézés",
    description:
      "Banki kapcsolatainkkal és hiteltanácsadókkal segítünk megtalálni a legjobb finanszírozási konstrukciót — gyorsan és átlátható feltételekkel.",
    icon: "Landmark",
    sortOrder: 3,
  },
];

/**
 * Default team members seeded on first run.
 * Names + roles are placeholders — replace via /admin/team.
 */
export const DEFAULT_TEAM_MEMBERS = [
  {
    name: "Kovács Klára",
    role: "Értékesítési vezető",
    email: "klara.kovacs@vatak.hu",
    phone: "+36 30 111 1111",
    sortOrder: 0,
  },
  {
    name: "Nagy László",
    role: "Műszaki projektvezető",
    email: "laszlo.nagy@vatak.hu",
    phone: "+36 30 222 2222",
    sortOrder: 1,
  },
  {
    name: "Szabó István",
    role: "Beruházási koordinátor",
    email: "istvan.szabo@vatak.hu",
    phone: "+36 30 333 3333",
    sortOrder: 2,
  },
  {
    name: "Tóth Kitti",
    role: "Ügyfélkapcsolat",
    email: "kitti.toth@vatak.hu",
    phone: "+36 30 444 4444",
    sortOrder: 3,
  },
  {
    name: "Molnár Marcell",
    role: "Operatív vezető",
    email: "marcell.molnar@vatak.hu",
    phone: "+36 30 555 5555",
    sortOrder: 4,
  },
  {
    name: "Csibor Balázs",
    role: "Hitelügyintézés",
    email: "balazs.csibor@vatak.hu",
    phone: "+36 30 666 6666",
    sortOrder: 5,
  },
];

/**
 * Default static pages seeded on first run.
 */
export const DEFAULT_STATIC_PAGES = [
  {
    slug: "adatvedelem",
    title: "Adatvédelmi tájékoztató",
    content: `# Adatvédelmi tájékoztató

Ez egy automatikusan generált helyettesítő tartalom. Cseréld le a tényleges adatvédelmi tájékoztatóval az admin felületen.

## 1. Adatkezelő

**VATÁK Ablakcentrum Kft.**
1051 Budapest, Október 6 utca 14. 1. em. 2A.
Email: info@vatak.hu

## 2. A kezelt adatok köre

A weboldalon a kapcsolatfelvételi űrlapon megadott személyes adatok (név, email cím, telefonszám, üzenet).

## 3. Adatkezelés célja

A kapcsolatfelvétel megválaszolása, az ügyfél tájékoztatása aktuális ingatlan-projektjeinkről.

## 4. Adatkezelés időtartama

Az ügyféllel való kapcsolattartás befejezésétől számított 5 év.

## 5. Az érintett jogai

- Tájékoztatáshoz való jog
- Helyesbítéshez való jog
- Törléshez való jog
- Adathordozhatósághoz való jog

További információért írjon az **info@vatak.hu** email címre.`,
  },
  {
    slug: "impresszum",
    title: "Impresszum",
    content: `# Impresszum

**VATÁK Ablakcentrum Kft.**

Teljes név: VATÁK Ablakcentrum Korlátolt Felelősségű Társaság
Székhely: 1051 Budapest, Október 6 utca 14. 1. em. 2A.
Adószám: 24069258-2-41
Alapítás éve: 2012
Főtevékenység: 4613 Fa-, építési anyag ügynöki nagykereskedelme

Email: info@vatak.hu
Telefon: +36 1 000 0000

## Tárhelyszolgáltató

Railway Corp.
548 Market St #38356
San Francisco, CA 94104, USA

---

*A cégjegyzékszám, telefonszám és tényleges email cím megadása szükséges — admin felületen szerkeszthető.*`,
  },
];
